import { Vector } from '@ardoq/graph';
import { PriorityQueue } from '../../misc/priorityqueue';

export type Cell = {
  col: number;
  row: number;
};

/*
  Packs a cell into a single number key. INVARIANT: callers must keep
  0 <= row < 1_000_000 and col >= 0. Outside that range hashes collide
  (e.g. cellHash({col, row: -1}) === cellHash({col: col - 1, row: 999_999})).
  This holds in practice because only cells accepted by isValid() are ever
  hashed, and the grid is bounded well within these limits.
*/
const cellHash = (cell: Cell): number => 1000000 * cell.col + cell.row;

/* information about an explored cell */
type CellInfo = {
  cameFrom: Cell | null;
  gScore: number;
};

/* a map keyed by cell value with specified missing value */
function cellMap<Type>(missing: Type) {
  const map = new Map<number, Type>();

  return {
    empty: () => map.size === 0,
    get: (cell: Cell) => map.get(cellHash(cell)) ?? missing,
    set: (cell: Cell, value: Type) => map.set(cellHash(cell), value),
    has: (cell: Cell) => map.has(cellHash(cell)),
    delete: (cell: Cell) => map.delete(cellHash(cell)),
    values: () => map.values(),
  };
}

/*
  Heuristic weight used for tie-breaking. Among cells with an equal fScore the
  search prefers the one with the lower heuristic (closer to the target), which
  cuts the number of expansions on uniform grids where many cells tie.

  The excess over 1 is small enough that h * (TIE_BREAK_WEIGHT - 1) < 1 for any
  realistic grid (h < 65536). With integer move costs and an integer heuristic
  — as the grid router uses — fScores are integers, so this never reorders cells
  with distinct fScores and optimal paths are preserved. For non-integer cost
  models it degrades to mildly weighted A*, bounding suboptimality by the weight.
*/
const TIE_BREAK_WEIGHT = 1 + 1 / 65536;

/* possible moves (could conveivably, but not usefully, be a parameter) */
const directions: Vector[] = [
  [-1, 0],
  [0, -1],
  [1, 0],
  [0, 1],
];

export function* astar(
  seeds: Cell[],
  isValid: (cell: Cell) => boolean,
  h: (cell: Cell) => number
): Generator<Cell, void, unknown> {
  const open = new PriorityQueue<Cell>(cellHash);
  const info = cellMap<CellInfo>({
    cameFrom: null,
    gScore: +Infinity,
  });

  /* initialise the solution seeds */

  for (const seed of seeds) {
    info.set(seed, { cameFrom: null, gScore: 0 });
    open.add(seed, h(seed));
  }

  /* keep looking for solution until we run out of options */

  let solution: Cell | null = null;

  while (open.size !== 0 && !solution) {
    const current = open.pop()!.value; // pop can't return undefined but linter doesn't know that

    if (h(current) === 0) {
      /* suggestion scored zero; that makes it a solution */

      solution = current;
    } else {
      /* worse than perfect not a solution; keep looking */

      /* current's info is loop-invariant; look it up once per expansion */
      const currentInfo = info.get(current);
      const cameFrom = currentInfo.cameFrom;

      /*
        The current direction of travel, as a unit vector (or [0, 0] when it
        cannot be determined). Kept as scalars to avoid allocating an array per
        expansion.
      */
      const currentDirCol = cameFrom ? current.col - cameFrom.col : 0;
      const currentDirRow = cameFrom ? current.row - cameFrom.row : 0;

      /*
        this loop pushes all the candidates onto open despite there being a good chance
        that one of them turns out getting popped as the next best candidate. it would
        be a linear optimisation to avoid the push/pop cycle where possible by using the best
        neighbor directly instead of going through a push/pop cycle
      */
      for (const direction of directions) {
        const neighbor = {
          col: current.col + direction[0],
          row: current.row + direction[1],
        };

        /*
          The dot product of two vectors is the cosine of the angle between them multiplied by the product of the
          magnitude of those vectors. Since both the current direction and direction are, by construction, unit vectors
          we can use the dot product directly as a measure of the corner.

          -1 indicates a U-turn, +1 indicates straight ahead and anything in-between some sort of corner. I'm just
          going to go ahead and quantise those as

            -Inf <  d < -0.5 basically a U-turn
            -0.5 <= d < +0.5 basically a corner
            +0.5 <= d < +Inf basically straight ahead

          If the current direction cannot be determined, [ 0, 0 ] is assumed which is handled as a corner

        */

        const dot = currentDirCol * direction[0] + currentDirRow * direction[1];

        if (dot < -0.5 || !isValid(neighbor)) {
          continue;
        }

        const costOfMoveToNeighbour = dot > 0.5 ? 1 : 10;

        const gScore = currentInfo.gScore + costOfMoveToNeighbour;

        if (gScore < info.get(neighbor).gScore) {
          /*
            fScore is biased by a tiny fraction of the heuristic so that, among
            cells with the same fScore, those closer to the target are expanded
            first. On uniform grids many cells share an fScore, so this markedly
            reduces the number of expansions. See TIE_BREAK_WEIGHT.
          */
          const fScore = gScore + h(neighbor) * TIE_BREAK_WEIGHT;

          info.set(neighbor, {
            cameFrom: current,
            gScore: gScore,
          });

          open.set(neighbor, fScore); // set or update the priority
        }
      }
    }
  }

  /* dump the solution */

  if (solution) {
    let current: Cell = solution;

    yield current;

    while (info.get(current).cameFrom) {
      current = info.get(current).cameFrom!;

      yield current;
    }
  }
}
